import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';
import { FormikValues } from 'formik';

import api from '../../../utils/api';

export interface CreateSpecializationPayload {
  id: number;
  name: string;
  code: string;
  description: string;
  academicProgramId: number;
  activeStatus: boolean;
}

interface SpecializationMutationResponse {
  code: number;
  message: string;
  data?: unknown;
}

export const GET_SPECIALIZATIONS_QUERY_KEY = ['get-specializations'];

export const useCreateSpecializationMutation = (handleClose?: () => void) => {
  const queryClient = useQueryClient();

  const createSpecialization = async (payload: CreateSpecializationPayload): Promise<SpecializationMutationResponse> => {
    return (await api.post('/Concentration', payload))?.data;
  };

  const mutation = useMutation({
    mutationKey: ['create-specialization'],
    mutationFn: createSpecialization,
  });

  const createSpecializationHandler = async (values: FormikValues, resetForm?: () => void) => {
    const payload: CreateSpecializationPayload = {
      id: Number(values?.id ?? 0),
      name: values?.name ?? '',
      code: values?.code ?? '',
      description: values?.description ?? '',
      academicProgramId: Number(values?.academicProgramId ?? values?.academicProgram ?? 0),
      activeStatus: Boolean(values?.activeStatus ?? false),
    };

    try {
      await mutation.mutateAsync(payload, {
        onSuccess: (data) => {
          notification.success({
            message: data?.message || 'Specialization created successfully',
          });

          queryClient.refetchQueries({
            queryKey: GET_SPECIALIZATIONS_QUERY_KEY,
          });

          resetForm?.();
          handleClose?.();
        },
      });
    } catch (error: any) {
      notification.error({
        message: 'Error',
        description: error?.response?.data?.message || error?.response?.data?.title || 'Failed to create specialization',
      });
    }
  };

  return {
    createSpecializationHandler,
    isLoading: mutation.isPending,
  };
};
